<template>
  <MentionCard
    :action-sheet-component="actionSheetComponent"
    base-icon="external-item"
    class="pointer"
    :list-title="mention.title"
    :mention="mention"
    :mentions="mentions"
    :no-highlighting="noHighlighting"
    :options="options"
    :selected="selected"
    :show-checkbox="showCheckbox"
    :stream="stream"
    :title="mention.title"
    @click="mentionClicked(mention)"
    @mention-selected="$emit('mention-selected', $event)"
  >
    <template #header>
      <span class="tw:text-base tw:font-bold tw:text-denim-900">
        {{ mention.source.name }}
      </span>

      <span class="tw:text-sm tw:text-denim-900">
        <TimeAgo
          :date="mention.timestamp"
          :timezone="mention.time_zone"
        />
        ·
        {{
          $t("external_item_mention_card.added_by", {
            createdBy: mention.created_by,
            createdAt: createdAt(mention),
            displayedTimezone: displayedTimezone(mention),
          })
        }}
      </span>
    </template>

    <template #body>
      <BaseLineClamp
        :lines="5"
        tag="span"
      >
        <span class="tw:text-base tw:leading-5 tw:text-denim-900">
          {{ mention.excerpts[0].text }}
        </span>
        <template #after="{ clamped }">
          <BaseStreemTooltip v-if="clamped">{{
            mention.excerpts[0].text
          }}</BaseStreemTooltip>
        </template>
      </BaseLineClamp>
    </template>

    <slot />

    <template #list-body>
      <p>
        {{ mention.excerpts[0].text }}
      </p>

      <div class="q-my-md">
        <p class="text-ds-denim-7">
          {{
            $t("external_item_mention_card.added_by", {
              createdBy: mention.created_by,
              createdAt: createdAt(mention),
              displayedTimezone: displayedTimezone(mention),
            })
          }}
        </p>
        <span
          class="hover-active bold q-my-md"
          @click.stop="mentionClicked(mention)"
        >
          {{ $t("external_item_mention_card.view_external_item") }}
        </span>
      </div>
    </template>

    <template #afterBody>
      <div class="tw:-ml-1">
        <QChip
          class="tw:mt-4 tw:bg-denim-900 tw:p-2 tw:font-bold tw:text-denim-900"
          dense
          text-color="white"
        >
          {{ $t("external_item_mention_card.external") }}
        </QChip>
      </div>

      <MentionCardInsights
        :mention-title="mention.title"
        :visible-widgets="visibleWidgets"
        :widget-keys="[sentimentWidgetKey]"
        @click.stop.prevent
      >
        <template #components>
          <PotentialAudienceReachWidget
            dense
            display-label
            :mention="mention"
          />

          <AdvertisingValueWidget
            dense
            :mention="mention"
          />

          <SentimentWidget
            :key="sentimentWidgetKey"
            dense
            :mention="mention"
            :stream="stream"
          />
        </template>
      </MentionCardInsights>
    </template>

    <slot />
  </MentionCard>
</template>

<script>
import AdvertisingValueWidget from "shared/components/AdvertisingValueWidget.vue";
import { BaseLineClamp, BaseStreemTooltip } from "shared/components/base";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import { MentionCard, props } from "shared/components/MentionCard";
import MentionCardInsights from "shared/components/MentionCardInsights.vue";
import PotentialAudienceReachWidget from "shared/components/PotentialAudienceReachWidget.vue";
import SentimentWidget from "shared/components/SentimentWidget.vue";
import useMention from "shared/composables/useMention";
import { formatIntlDate, parseDate, shortTimezone } from "shared/helpers/date";

export default {
  name: "ExternalItemMentionCard",
  components: {
    BaseLineClamp,
    SentimentWidget,
    MentionCardInsights,
    AdvertisingValueWidget,
    PotentialAudienceReachWidget,
    MentionCard,
    TimeAgo,
    BaseStreemTooltip,
  },
  props,
  emits: ["mention-selected"],
  setup(mentionProps, context) {
    const { mentionClicked } = useMention(mentionProps, context);

    return { mentionClicked };
  },
  data() {
    return {
      sentimentValue: null,
    };
  },
  computed: {
    sentimentWidgetKey() {
      return `sentiment-widget-${this.mention.id}-${this.sentimentValue}`;
    },
    visibleWidgets() {
      const widgets = ["SentimentWidget"];

      if (this.mention.audience) {
        widgets.push("PotentialAudienceReachWidget");
      }

      if (this.mention.advertising_value) {
        widgets.push("AdvertisingValueWidget");
      }

      return widgets;
    },
  },
  methods: {
    displayedTimezone(mention) {
      return shortTimezone(parseDate(mention.timestamp), mention.time_zone);
    },
    createdAt(mention) {
      return formatIntlDate(new Date(mention.created_at), {
        timeStyle: "short",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.mention-stream-view-card) {
  border-top: 1px solid $ds-denim-3;
  border-right: 1px solid $ds-denim-3;
  border-bottom: 1px solid $ds-denim-3;
}
</style>
